import { AxiosError } from 'axios';
import { useCustomerContext } from 'pages/Customer/CustomerContext';
import CreateResellerContractModal from 'pages/Reseller/components/CreateResellerContractModal';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { getContractById, listContractsByParams } from 'services/contract';
import { getContractTypesCached } from 'services/contractType.cached';
import { ListContractsRequestData } from 'services/interfaces/contract';
import { Mixpanel } from 'services/mixpanel';
import { Contract, IContract } from 'shared/models/contract';
import ChangeManyDueDateModal from './components/modals/ChangeManyDueDateModal';
import ChangeManyPaymentMethodModal from './components/modals/ChangeManyPaymentMethodModal';
import ChangePlanModal from './components/modals/ChangePlanModal';
import ContractModalContext from './components/modals/CreateContractModal';
import OrderModal from './components/modals/OrderModal';
import Financial from './Financial';

interface FinancialContextData {
  openOrderModal(n: string): void;
  openChangePaymentMethodModal(): void;
  onCloseChangePaymentMethodModal(): void;
  changePaymentMethodModalOpen: boolean;
  openChangeDueDateModal(): void;
  onCloseChangeDueDateModal(): void;
  changeDueDateModalOpen: boolean;
  openChangeManyDueDateModal(): void;
  onCloseChangeManyDueDateModal(): void;
  changeManyDueDateModalOpen: boolean;
  openChangeManyPaymentMethodModal(): void;
  onCloseChangeManyPaymentMethodModal(): void;
  changeManyPaymentMethodModalOpen: boolean;
  openChangePlanModal(c: Contract): void;
  openAddServicesModal(c: Contract): void;
  isActive: boolean;
  tabIndex: number;
  setTabIndex: (index: number) => void;
  displayedContract: Contract | null;
  openContractDetails: (contract: Contract) => void;
  onChangeTab?: (index?: number) => void;
  loadContracts: (
    params: ListContractsRequestData,
  ) => Promise<{ contracts: Contract[]; results: IContract[]; total: number }>;
  reloadContracts: (back?: boolean, contract?: Contract) => Promise<void>;
  contracts: Contract[];
  openCreateContractModal(): void;
  openResellerContractModal(): void;
}

const Context = createContext<FinancialContextData>({} as FinancialContextData);

export const useFinancialContext = () => useContext(Context);

const FinancialContext = ({ isActive }: { isActive: boolean }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [displayedContract, setDisplayedContract] = useState<Contract | null>(
    null,
  );
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [orderModalToken, setOrderModalToken] = useState<string>();

  const [changePlanModalOpen, setChangePlanModalOpen] = useState(false);
  const [changePlanModalData, setChangePlanModalData] = useState<Contract>();
  const { customer } = useCustomerContext();

  const [changePaymentMethodModalOpen, setChangePaymentMethodModalOpen] =
    useState(false);

  const [changeDueDateModalOpen, setChangeDueDateModalOpen] = useState(false);
  const [changeManyDueDateModalOpen, setChangeManyDueDateModalOpen] =
    useState(false);
  const [
    changeManyPaymentMethodModalOpen,
    setChangeManyPaymentMethodModalOpen,
  ] = useState(false);

  const [createContractOpen, setCreateContractOpen] = useState(false);
  const [resellerContractOpen, setResellerContractOpen] = useState(false);

  const [addServicesOpen, setAddServicesOpen] = useState(false);
  const [addServicesContract, setAddServicesContract] = useState<Contract>();

  const [lastListParams, setLastListParams] =
    useState<ListContractsRequestData>({
      limit: 20,
      offset: 0,
      includeDeleted: true,
    });

  const reloadReqId = useRef(0);

  const openCreateContractModal = () => setCreateContractOpen(true);
  const closeCreateContractModal = () => {
    setCreateContractOpen(false);
    void reloadContracts(false);
  };

  const openResellerContractModal = () => setResellerContractOpen(true);
  const closeResellerContractModal = () => {
    setResellerContractOpen(false);
    void reloadContracts(false);
  };

  const openAddServicesModal = (contract: Contract) => {
    setAddServicesContract(contract);
    setAddServicesOpen(true);
  };

  const closeAddServicesModal = () => {
    setAddServicesOpen(false);
    setAddServicesContract(undefined);
    void reloadContracts(false);
  };

  const openChangePlanModal = (contract?: Contract) => {
    if (contract) setChangePlanModalData(contract);
    setChangePlanModalOpen(true);
  };

  const openChangePaymentMethodModal = () =>
    setChangePaymentMethodModalOpen(true);

  const onCloseChangePaymentMethodModal = () => {
    setChangePaymentMethodModalOpen(false);
    void reloadContracts(false);
  };

  const openChangeDueDateModal = () => setChangeDueDateModalOpen(true);

  const onCloseChangeDueDateModal = () => {
    setChangeDueDateModalOpen(false);
    void reloadContracts(false);
  };

  const openChangeManyDueDateModal = () => setChangeManyDueDateModalOpen(true);

  const onCloseChangeManyDueDateModal = () => {
    setChangeManyDueDateModalOpen(false);
    void loadContracts(lastListParams);
  };

  const openChangeManyPaymentMethodModal = () =>
    setChangeManyPaymentMethodModalOpen(true);

  const onCloseChangeManyPaymentMethodModal = () => {
    setChangeManyPaymentMethodModalOpen(false);
    void loadContracts(lastListParams);
  };

  const openOrderModal = (orderToken?: string) => {
    Mixpanel.track('customers-financial-order-opened-modal');
    if (orderToken) setOrderModalToken(orderToken);
    setOrderModalOpen(true);
  };

  const onCloseOrderModal = () => {
    setOrderModalToken(undefined);
    setOrderModalOpen(false);
    void reloadContracts(false);
  };

  const openContractDetails = (contract: Contract): void => {
    setTabIndex(1);
    setDisplayedContract(contract);
  };

  const loadContracts = useCallback(
    async (params: ListContractsRequestData) => {
      const payload: ListContractsRequestData = {
        ...params,
        customer: customer?.id ?? params.customer,
      };

      if (!payload.customer) {
        return { results: [], total: 0, contracts: [] };
      }

      const response = await listContractsByParams(payload);

      const contracts = response.data.results.map(
        (contractResponse: IContract) => new Contract(contractResponse),
      );

      setLastListParams(payload);
      setContracts(contracts);
      return { ...response.data, contracts };
    },
    [customer?.id],
  );

  const reloadContracts = useCallback(
    async (back?: boolean, contractArg?: Contract) => {
      const reqId = ++reloadReqId.current;
      const selectedId = (contractArg ?? displayedContract)?.id;

      const listPromise = loadContracts(lastListParams);

      if (back) {
        await listPromise;
        if (reqId !== reloadReqId.current) return;
        setDisplayedContract(null);
        if (tabIndex !== 0) setTabIndex(0);
        return;
      }

      if (!selectedId) {
        await listPromise;
        return;
      }

      const [response, detail] = await Promise.all([
        listPromise,
        getContractById(selectedId, true).catch((e: AxiosError) => {
          console.error(
            'Erro ao carregar detalhes do contrato',
            e?.response?.data
              ? JSON.stringify(e?.response?.data, null, 2)
              : JSON.stringify(e?.response, null, 2),
          );
          return null;
        }),
      ]);

      if (reqId !== reloadReqId.current) return;

      const fromList = response.contracts.find((c) => c.id === selectedId);
      if (fromList) {
        setDisplayedContract(fromList);
        if (tabIndex !== 1) setTabIndex(1);
        return;
      }

      if ((detail as any)?.data) {
        setDisplayedContract(new Contract((detail as any).data));
        if (tabIndex !== 1) setTabIndex(1);
        return;
      }

      setDisplayedContract(null);
      if (tabIndex !== 0) setTabIndex(0);
    },
    [displayedContract, lastListParams, loadContracts, tabIndex],
  );

  const onCloseChangePlanModal = () => {
    setChangePlanModalData(undefined);
    setChangePlanModalOpen(false);
    void reloadContracts(false);
  };

  const prevIsActive = useRef(isActive);
  useEffect(() => {
    const becameActive = !prevIsActive.current && isActive;
    prevIsActive.current = isActive;

    if (!becameActive) return;
    if (displayedContract) return;
    if (tabIndex !== 0) setTabIndex(0);
  }, [isActive, displayedContract, tabIndex]);

  useEffect(() => {
    getContractTypesCached().catch((e: AxiosError) => {
      console.error(
        'Erro ao carregar tipos de contrato',
        e?.response?.data
          ? JSON.stringify(e?.response?.data, null, 2)
          : JSON.stringify(e?.response, null, 2),
      );
    });
  }, []);

  const data: FinancialContextData = {
    openOrderModal,
    openChangePaymentMethodModal,
    openChangePlanModal,
    onCloseChangePaymentMethodModal,
    changePaymentMethodModalOpen,
    openChangeDueDateModal,
    onCloseChangeDueDateModal,
    changeDueDateModalOpen,
    openChangeManyDueDateModal,
    onCloseChangeManyDueDateModal,
    changeManyDueDateModalOpen,
    openChangeManyPaymentMethodModal,
    onCloseChangeManyPaymentMethodModal,
    changeManyPaymentMethodModalOpen,
    isActive,
    tabIndex,
    setTabIndex,
    displayedContract,
    openContractDetails,
    reloadContracts,
    loadContracts,
    contracts,
    openCreateContractModal,
    openAddServicesModal,
    openResellerContractModal,
  };

  return (
    <Context.Provider value={data}>
      <Financial />
      <OrderModal
        isOpen={orderModalOpen}
        onClose={onCloseOrderModal}
        token={orderModalToken}
      />
      <ChangePlanModal
        isOpen={changePlanModalOpen}
        onClose={onCloseChangePlanModal}
        contract={changePlanModalData}
      />
      <ContractModalContext
        isOpen={createContractOpen}
        onClose={closeCreateContractModal}
      />
      <ContractModalContext
        isOpen={addServicesOpen}
        onClose={closeAddServicesModal}
        contract={addServicesContract}
      />
      <CreateResellerContractModal
        isOpen={resellerContractOpen}
        onClose={closeResellerContractModal}
      />
      <ChangeManyDueDateModal
        isOpen={changeManyDueDateModalOpen}
        onClose={onCloseChangeManyDueDateModal}
        contracts={contracts}
      />
      <ChangeManyPaymentMethodModal
        isOpen={changeManyPaymentMethodModalOpen}
        onClose={onCloseChangeManyPaymentMethodModal}
        contracts={contracts}
      />
    </Context.Provider>
  );
};

export default FinancialContext;
